export interface ReadsApiItem {
  article_id: string;
  last_read_at: string;
  total_seconds_spent: number;
}

export interface BumpThresholds {
  minSeconds: number;
  delayMinutes: number;
}

/*
 * Fallback score penalty for a read article, used only when neither the
 * DrEdition placeholder nor publication-context supplies one.
 */
export const READ_PENALTY = 10;

/*
 * The reads API returns one row per read session, so the same article_id can
 * repeat. Aggregate per id (sum of seconds spent across all sessions, most
 * recent read) before applying the eligibility gates, so a freshly re-read
 * article is treated as not-yet-settled rather than bumped on stale session
 * data.
 */
export function getBumpableReadIds (items: ReadsApiItem[], now: number, { minSeconds, delayMinutes }: BumpThresholds): Set<string> {
  const aggregated = new Map<string, { sumSeconds: number; latestReadAt: number }>();

  for (const item of items || []) {
    const articleId = item?.article_id;
    if (!articleId) { continue; }

    const seconds = Number(item.total_seconds_spent);
    const readAt = Date.parse(item.last_read_at);
    if (!Number.isFinite(seconds) || !Number.isFinite(readAt)) { continue; }

    const existing = aggregated.get(articleId);
    if (!existing) {
      aggregated.set(articleId, { sumSeconds: seconds, latestReadAt: readAt });
    } else {
      existing.sumSeconds += seconds;
      existing.latestReadAt = Math.max(existing.latestReadAt, readAt);
    }
  }

  const delayMs = delayMinutes * 60_000;
  const bumpIds = new Set<string>();
  aggregated.forEach(({ sumSeconds, latestReadAt }, articleId) => {
    if (sumSeconds >= minSeconds && latestReadAt < now - delayMs) {
      bumpIds.add(articleId);
    }
  });

  return bumpIds;
}

/*
 * score(item) = originalIndex + (penalty if read). Sorting ascending by score
 * demotes read articles by `penalty` slots instead of sinking them to the
 * bottom; the sort is stable, so equal scores keep their original relative
 * order (the tie-break the ticket calls for). Never mutates or drops items.
 */
export function rankByReadPenalty<T extends { id: string }> (items: T[], readIds: Set<string>, penalty: number = READ_PENALTY): T[] {
  if (!readIds || readIds.size === 0) { return items; }

  return items
    .map((item, index) => ({ item, score: index + (item?.id && readIds.has(item.id) ? penalty : 0) }))
    .sort((a, b) => a.score - b.score)
    .map(({ item }) => item);
}
